import { useState, type FormEvent } from 'react'
import type { Result } from '../api/client'
import type { User } from '../api/types'
import { Centered } from '../components/Centered'

/** Mirrors the server's own rules, so an obvious mistake is caught without a round trip. */
const MIN_PASSWORD = 10
const CALLSIGN = /^[A-Za-z0-9._-]{2,24}$/

/**
 * Creating an account from the dashboard.
 *
 * This existed only in the app for a long time, which left a hole in the setup path nobody noticed
 * until somebody stood up a server and had nowhere to make the first account: the instance
 * administrator is told to create an organisation from this dashboard, and could not get an
 * account to sign in with unless they also had a phone to hand.
 *
 * Shown only when the server says it is accepting registrations. A club that has closed sign-ups
 * gets the sign-in screen alone, with nothing to suggest otherwise.
 */
export function RegisterScreen({
  serverName,
  onRegister,
  onCancel,
}: {
  serverName: string
  onRegister: (details: {
    email: string
    password: string
    callsign: string
    fullName?: string
  }) => Promise<Result<User>>
  onCancel: () => void
}) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [callsign, setCallsign] = useState('')
  const [fullName, setFullName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  function localProblem(): string | null {
    if (!email.trim()) return 'An email address is required.'
    if (password.length < MIN_PASSWORD) {
      return `Password must be at least ${MIN_PASSWORD} characters.`
    }
    if (!CALLSIGN.test(callsign.trim())) {
      return 'Callsign must be 2–24 characters: letters, digits, dot, dash or underscore.'
    }
    return null
  }

  async function submit(event: FormEvent) {
    event.preventDefault()

    // Checked here as well as on the server, because the round trip is the slow part and these are
    // the three mistakes people actually make. The server remains the authority.
    const problem = localProblem()
    if (problem) {
      setError(problem)
      return
    }

    setBusy(true)
    setError(null)

    const result = await onRegister({
      email: email.trim(),
      password,
      callsign: callsign.trim(),
      fullName: fullName.trim() || undefined,
    })

    if (!result.ok) {
      setBusy(false)
      setError(result.error.message)
    }
    // On success the shell moves on and this screen is unmounted.
  }

  return (
    <Centered>
      <form className="card stack" onSubmit={submit} style={{ width: 380 }}>
        <div>
          <h1>Create an account</h1>
          {serverName && (
            <p className="muted" style={{ margin: '4px 0 0' }}>
              {serverName}
            </p>
          )}
        </div>

        {/*
          Stated unconditionally, because nothing the client can see says whether this instance
          already has accounts. On a fresh server it is the most important thing on the page; on an
          established one it is a true sentence about a rule, which is a fair price for not adding
          an endpoint that reports how empty somebody's server is.
        */}
        <div className="banner info">
          The first account on a new server becomes its administrator, and is the only one that can
          create organisations.
        </div>

        <div className="field">
          <label htmlFor="register-email">Email</label>
          <input
            id="register-email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="username"
            autoFocus
          />
        </div>

        <div className="field">
          <label htmlFor="register-callsign">Callsign</label>
          <input
            id="register-callsign"
            type="text"
            value={callsign}
            onChange={(event) => setCallsign(event.target.value)}
            autoComplete="nickname"
          />
          <p className="muted" style={{ margin: '4px 0 0', fontSize: '0.85em' }}>
            Shown on the map. Letters, digits, dot, dash and underscore.
          </p>
        </div>

        <div className="field">
          <label htmlFor="register-name">Full name</label>
          <input
            id="register-name"
            type="text"
            value={fullName}
            onChange={(event) => setFullName(event.target.value)}
            autoComplete="name"
          />
          <p className="muted" style={{ margin: '4px 0 0', fontSize: '0.85em' }}>
            Optional.
          </p>
        </div>

        <div className="field">
          <label htmlFor="register-password">Password</label>
          <input
            id="register-password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="new-password"
          />
          <p className="muted" style={{ margin: '4px 0 0', fontSize: '0.85em' }}>
            At least {MIN_PASSWORD} characters.
          </p>
        </div>

        {error && <div className="banner danger">{error}</div>}

        <button type="submit" disabled={busy}>
          {busy ? 'Creating…' : 'Create account'}
        </button>

        <button type="button" className="ghost" onClick={onCancel}>
          I already have an account
        </button>
      </form>
    </Centered>
  )
}
